import { useCallback, useMemo } from "react";
import { runInAction } from "mobx";

import { useBlock } from "../block-store";
import { SourceType } from "../SourcesField/SelectSource";

// Utility functions for handling nested object access
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getNestedValue(obj: any, path: string): any {
  // First check if the exact path exists as a key
  if (obj?.[path] !== undefined) {
    return obj[path];
  }

  // If not, try to access it as a nested path
  const keys = path.split(".");

  // Try progressively longer suffixes to handle flattened keys (remove from start)
  for (let i = 1; i < keys.length; i += 1) {
    const flatKey = keys.slice(i).join(".");
    const parentPath = keys.slice(0, i);

    // Navigate to the parent
    let current = obj;
    for (const key of parentPath) {
      if (current?.[key] === undefined) {
        current = null;
        break;
      }
      current = current[key];
    }

    if (current?.[flatKey] !== undefined) {
      return current[flatKey];
    }
  }

  // Fall back to standard nested access
  return keys.reduce((current, key) => current?.[key], obj);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function setNestedValue(obj: any, path: string, value: any): any {
  // If value is undefined or null, we need to handle deletion/cleanup
  const shouldDelete = value === undefined || value === null || value === "";

  // First check if we should use the path as a literal key
  const keys = path.split(".");

  // Try to find if any suffix of the path exists as a flattened key (remove from start)
  for (let i = 1; i < keys.length; i += 1) {
    const flatKey = keys.slice(i).join(".");
    const parentPath = keys.slice(0, i);

    // Navigate to the parent
    let parent = obj;
    for (const key of parentPath) {
      if (parent[key] === undefined || parent[key] === null) {
        if (shouldDelete) {
          // Don't create structure if we're deleting
          return obj;
        }
        parent[key] = {};
      } else if (typeof parent[key] !== "object") {
        if (shouldDelete) {
          return obj;
        }
        parent[key] = {};
      }
      parent = parent[key];
    }

    // Check if this level uses flattened keys
    if (parent[flatKey] !== undefined || !shouldDelete) {
      // Use the flattened key at this level
      if (shouldDelete) {
        delete parent[flatKey];
        // Clean up empty parent objects
        cleanupEmptyObjects(obj, parentPath);
      } else {
        parent[flatKey] = value;
      }
      return obj;
    }
  }

  // Standard nested structure creation
  const lastKey = keys.pop()!;

  // Navigate to or create the parent structure
  let current = obj;
  for (const key of keys) {
    if (current[key] === undefined || current[key] === null) {
      if (shouldDelete) {
        // Don't create structure if we're deleting
        return obj;
      }
      current[key] = {};
    } else if (typeof current[key] !== "object") {
      if (shouldDelete) {
        return obj;
      }
      current[key] = {};
    }
    current = current[key];
  }

  // Set or delete the final value
  if (shouldDelete) {
    delete current[lastKey];
    // Clean up empty parent objects
    cleanupEmptyObjects(obj, keys);
  } else {
    current[lastKey] = value;
  }

  return obj;
}

// Helper function to clean up empty objects in the path
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function cleanupEmptyObjects(obj: any, pathKeys: string[]): void {
  for (let i = pathKeys.length - 1; i >= 0; i -= 1) {
    const partialPath = pathKeys.slice(0, i + 1);
    let current = obj;

    for (let j = 0; j < partialPath.length - 1; j += 1) {
      current = current[partialPath[j]];
      if (!current) {
        return;
      }
    }

    const lastKey = partialPath[partialPath.length - 1];
    if (current[lastKey] && typeof current[lastKey] === "object") {
      const isEmpty = Object.keys(current[lastKey]).length === 0;
      if (isEmpty && partialPath.length > 1) {
        // Only delete empty objects if they're not top-level
        delete current[lastKey];
      } else if (!isEmpty) {
        // Stop cleanup if object is not empty
        break;
      }
    }
  }
}

export type SourceConfig =
  | { type: "single"; key?: string }
  | { type: "double"; firstKey?: string; secondKey?: string }
  | { type: "array"; min?: number; max?: number }
  | { type: "mapped"; keys: string[] };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface UseSourceSelectionOptions<TConfiguration = any> {
  config: SourceConfig;
  availableSources: Record<SourceType, string[]>;
  getSourcesFromConfiguration?: (configuration: TConfiguration) => Record<string, string[]>;
  setSourcesInConfiguration?: (
    configuration: TConfiguration,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sources: any,
  ) => TConfiguration;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useSourceSelection<TConfiguration = any>(
  options: UseSourceSelectionOptions<TConfiguration>,
) {
  const blockStore = useBlock<TConfiguration>();
  const { config, availableSources } = options;

  // Single source handling
  const handleSingleSourceChange = useCallback(
    (value: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const key = (config as any).key || "source";

      runInAction(() => {
        // Handle nested keys like 'input_variables.key'
        if (key.includes(".")) {
          const newConfiguration = { ...blockStore.configuration };
          setNestedValue(newConfiguration, key, value);
          blockStore.setConfiguration(newConfiguration as TConfiguration);
        } else {
          // Handle regular keys
          blockStore.setConfiguration({
            ...blockStore.configuration,
            [key]: value,
          } as TConfiguration);
        }
      });
    },
    [blockStore, config],
  );

  // Double source handling
  const handleDoubleSourceChange = useCallback(
    (index: 0 | 1, value: string) => {
      const doubleConfig = config as {
        type: "double";
        firstKey?: string;
        secondKey?: string;
      };
      const firstKey = doubleConfig.firstKey || "sources";
      const secondKey = doubleConfig.secondKey || "sources";

      runInAction(() => {
        if (firstKey === secondKey) {
          // Array-based storage
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const sources = (blockStore.configuration as any)?.[firstKey] || ["", ""];
          const newSources = [...sources];
          newSources[index] = value;
          blockStore.setConfiguration({
            ...blockStore.configuration,
            [firstKey]: newSources,
          } as TConfiguration);
        } else {
          // Separate field storage
          const key = index === 0 ? firstKey : secondKey;
          blockStore.setConfiguration({
            ...blockStore.configuration,
            [key]: value,
          } as TConfiguration);
        }
      });
    },
    [blockStore, config],
  );

  // Array source handling
  const handleArraySourceChange = useCallback(
    (index: number, value: string) => {
      runInAction(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sources = (blockStore.configuration as any)?.sources || [""];
        const newSources = [...sources];
        newSources[index] = value;

        blockStore.setConfiguration({
          ...blockStore.configuration,
          sources: newSources,
        } as TConfiguration);
      });
    },
    [blockStore],
  );

  const handleArraySourceAdd = useCallback(() => {
    runInAction(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sources = (blockStore.configuration as any)?.sources || [""];
      blockStore.setConfiguration({
        ...blockStore.configuration,
        sources: [...sources, ""],
      } as TConfiguration);
    });
  }, [blockStore]);

  const handleArraySourceRemove = useCallback(
    (index: number) => {
      runInAction(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sources = (blockStore.configuration as any)?.sources || [""];
        blockStore.setConfiguration({
          ...blockStore.configuration,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          sources: sources.filter((_: any, i: number) => i !== index),
        } as TConfiguration);
      });
    },
    [blockStore],
  );

  // Get current values based on config type
  const values = useMemo(() => {
    if (!blockStore.configuration) {
      return null;
    }

    switch (config.type) {
      case "single": {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const key = (config as any).key || "source";

        // Handle nested keys like 'input_variables.key'
        if (key.includes(".")) {
          return getNestedValue(blockStore.configuration, key) || null;
        }
        // Handle regular keys
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (blockStore.configuration as any)[key] || null;
      }
      case "double": {
        const doubleConfig = config as {
          type: "double";
          firstKey?: string;
          secondKey?: string;
        };
        const firstKey = doubleConfig.firstKey || "sources";
        const secondKey = doubleConfig.secondKey || "sources";

        if (firstKey === secondKey) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const sources = (blockStore.configuration as any)?.[firstKey] || ["", ""];
          return { first: sources[0], second: sources[1] };
        }
        return {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          first: (blockStore.configuration as any)[firstKey] || null,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          second: (blockStore.configuration as any)[secondKey] || null,
        };
      }

      case "array":
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (blockStore.configuration as any)?.sources || [""];

      default:
        return null;
    }
  }, [blockStore.configuration, config]);

  // Filter available sources for array type (prevent duplicates)
  const getAvailableSourcesForIndex = useCallback(
    (currentIndex: number) => {
      if (config.type !== "array") {
        return availableSources;
      }

      const sources = (values as string[]) || [""];
      const chosenSources = sources
        .filter((_: string, index: number) => index !== currentIndex)
        .filter((source: string) => source && source.trim() !== "");

      const filtered: Record<SourceType, string[]> = {} as Record<SourceType, string[]>;

      Object.entries(availableSources).forEach(([sourceType, sourceList]) => {
        const filteredList = sourceList.filter(source => !chosenSources.includes(source));
        filtered[sourceType as SourceType] = filteredList;
      });

      return filtered;
    },
    [config.type, values, availableSources],
  );

  return {
    values,
    handleSingleSourceChange,
    handleDoubleSourceChange,
    handleArraySourceChange,
    handleArraySourceAdd,
    handleArraySourceRemove,
    getAvailableSourcesForIndex,
  };
}
